import { useEffect, useState } from 'react';
import { RESOURCE_CHANGED_EVENT } from './use-copilot';

export const FLASH_MS = 3600;

/**
 * Keys that should blink because the copilot just changed them. `keysOf(detail)` maps a
 * `resource.changed` event detail ({ appId, kind, action, id, name }) to the keys to flash;
 * each key stays in the returned Map (key -> pending changes) for `durationMs` after its
 * latest change.
 */
export default function useResourceFlash(keysOf, durationMs = FLASH_MS) {
    const [flashing, setFlashing] = useState(() => new Map());

    useEffect(() => {
        const timers = new Set();
        const onChanged = event => {
            let keys = [];
            try {
                keys = (keysOf(event?.detail || {}) || []).filter(Boolean);
            } catch (_) {
                return;
            }
            if (!keys.length) return;
            setFlashing(current => {
                const next = new Map(current);
                keys.forEach(key => next.set(key, (next.get(key) || 0) + 1));
                return next;
            });
            const timer = setTimeout(() => {
                timers.delete(timer);
                setFlashing(current => {
                    const next = new Map(current);
                    keys.forEach(key => {
                        const count = (next.get(key) || 0) - 1;
                        if (count > 0) next.set(key, count); else next.delete(key);
                    });
                    return next;
                });
            }, durationMs);
            timers.add(timer);
        };
        window.addEventListener(RESOURCE_CHANGED_EVENT, onChanged);
        return () => {
            window.removeEventListener(RESOURCE_CHANGED_EVENT, onChanged);
            timers.forEach(clearTimeout);
        };
    }, [keysOf, durationMs]);

    return flashing;
}
